import { UserRole } from '../../types/user';

/**
 * HA-285: Categories visible to a Client in the Help Center.
 *
 * Hidden by exclusion: 'clients' (Managing Clients), 'billing'
 * (Billing & Subscriptions), 'content-tools' (Training programs /
 * forms / recipes — practitioner-authoring concepts). The
 * remaining 5 categories are patient-relevant.
 */
export const CLIENT_VISIBLE_CATEGORY_SLUGS: ReadonlyArray<string> = [
  'getting-started',
  'appointments',
  'messaging',
  'telehealth',
  'settings-security',
];

/**
 * HA-285: Patient-perspective copy overrides. The primary
 * category list ships practitioner-perspective subtitles
 * ("Secure messaging with clients"). For Client viewers, swap
 * to a patient-perspective subtitle so the catalog reads
 * correctly. Only `description` is overridden today; `name`
 * stays the same (no visible practitioner-coded names in the
 * 5 visible slugs).
 */
export const CLIENT_CATEGORY_OVERRIDES: Record<string, { description?: string }> = {
  'getting-started': { description: 'Get oriented with your patient portal' },
  appointments: { description: 'View and manage your appointments' },
  messaging: { description: 'Secure messaging with your practitioner' },
  telehealth: { description: 'Joining video sessions with your practitioner' },
  'settings-security': { description: 'Account settings, security, and preferences' },
};

/**
 * Fail-closed: undefined / null role (mid-bootstrap before
 * AuthContext resolves) is treated as Client so practitioner
 * content can't flash before the role lands.
 */
export const isClientRole = (role?: UserRole | string | null): boolean =>
  role === undefined || role === null || role === UserRole.Client || role === 'Client';

/**
 * Returns the slug whitelist if the role is Client; `null`
 * means "no filter" — Practitioner / PracticeAdmin / SuperUser
 * see every category.
 */
export const getVisibleCategorySlugs = (
  role?: UserRole | string | null
): ReadonlyArray<string> | null => (isClientRole(role) ? CLIENT_VISIBLE_CATEGORY_SLUGS : null);

/**
 * Apply patient-perspective subtitle overrides to a category
 * list. No-op for non-Client roles.
 */
export const applyClientCategoryOverrides = <
  T extends { categorySlug: string; description: string },
>(
  cats: T[],
  role?: UserRole | string | null
): T[] => {
  if (!isClientRole(role)) return cats;

  return cats.map((c) => {
    const o = CLIENT_CATEGORY_OVERRIDES[c.categorySlug];

    return o?.description ? { ...c, description: o.description } : c;
  });
};
